<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('monthlySaleItem.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('monthlySaleItem.pageTitle'),
          active: true,
        },
      ]" />
    </div>
    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <ts-card>
            <ts-card-wrapper>
              <ts-loading-banner :loading="loading">
                <form>
                  <fieldset>
                    <legend>{{ $t('monthlySaleItem.criteria') }}</legend>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('monthlySaleItem.saleDate')
                          }}</label>
                          <a-range-picker v-model="model.date_range" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY"
                            :defaultValue="rangeDate" :defaultPickerValue="rangeDate" :ranges="{
                              Today: [moment(), moment()],
                              'This Month': [
                                moment().startOf('month'),
                                moment().endOf('month'),
                              ],
                            }">
                            <template slot="renderExtraFooter"></template>
                          </a-range-picker>
                          <div class="validate" v-if="errors.has('date_range')">
                            {{ errors.first('date_range') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('monthlySaleItem.branchName') }}</label>
                          <a-select v-model="model.branch_id" mode="multiple" :options="branches" class="ts-w-full"
                            placeholder="All"></a-select>
                          <div class="validate" v-if="errors.has('branch_id')">
                            {{ errors.first('branch_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{
                            $t('stockTransferReport.warehouse')
                          }}</label>
                          <a-select v-model="model.location_id" :options="locations" mode="multiple" placeholder="All"
                            class="ts-w-full"></a-select>
                          <div class="validate" v-if="errors.has('location_id')">
                            {{ errors.first('location_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('monthlySaleItem.cashier') }}</label>
                          <a-select v-model="model.cashier_id" :options="cashiers" mode="multiple" showSearch
                            :filterOption="(input, option) =>
                              option.componentOptions.children[0].text
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                              " :maxTagCount="3" class="ts-w-full" placeholder="All">
                          </a-select>
                          <div class="validate" v-if="errors.has('cashier_id')">
                            {{ errors.first('cashier_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('monthlySaleItem.category') }}</label>
                          <a-select v-model="model.category_id" mode="multiple" :options="categories"
                            @change="onCategoryChange" placeholder="All" class="ts-w-full"></a-select>
                          <div class="validate" v-if="errors.has('category_id')">
                            {{ errors.first('category_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('monthlySaleItem.group') }}</label>
                          <a-select v-model="model.pro_group_id" mode="multiple" :options="groups" class="ts-w-full"
                            placeholder="All" @change="() => (model.product_id = [])"></a-select>
                          <div class="validate" v-if="errors.has('pro_group_id')">
                            {{ errors.first('pro_group_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label>{{ $t('monthlySaleItem.product') }}</label>
                          <a-select v-model="model.product_id" mode="multiple" :loading="on_search_product"
                            @search="onProductSearch" :show-arrow="false" :filter-option="true"
                            :not-found-content="null" :filterOption="(input, option) =>
                              option.componentOptions.children[0].text
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                              ">
                            <a-select-option v-for="product in productList" :key="product.value" :value="product.value">
                              {{ product.label }}
                            </a-select-option>
                          </a-select>
                          <div class="validate" v-if="errors.has('product_id')">
                            {{ errors.first('product_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6 ts-text-right">
                        <ts-button outline color="success" class="btn-block" @click.prevent="exportExcel"
                          :waiting="exporting"><i class="far fa-file-excel"></i>
                          {{ $t('export') }}</ts-button>
                      </div>
                      <div class="col-md-6 ts-text-right">
                        <ts-button color="danger" outline class="btn-block" @click.prevent="preview"
                          :waiting="waiting"><i class="far fa-file-pdf"></i>
                          {{ $t('preview') }}</ts-button>
                      </div>
                    </div>
                  </fieldset>
                </form>
              </ts-loading-banner>
            </ts-card-wrapper>
          </ts-card>
        </div>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('monthlySaleItem.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'monthlySaleItem',
  data() {
    return {
      waiting: false,
      loading: false,
      exporting: false,
      on_search_product: false,
      errors: new Errors(),
      branches: [],
      locations: [],
      cashiers: [],
      groups: [],
      categories: [],
      productList: [],
      src: '',
      rangeDate: [moment(), moment()],
      model: {
        date_range: [
          moment().format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY')
        ],
        branch_id: undefined,
        location_id: undefined,
        cashier_id: undefined,
        pro_group_id: [],
        category_id: [],
        product_id: []
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    ...mapActions('report/resource', [
      'getBranch',
      'getLocations',
      'getCashier',
      'productGroup',
      'productCategory',
      'productProfileLocation'
    ]),
    moment,
    async fetchData() {
      this.loading = true
      await this.getCashier().then(response => {
        this.cashiers = response.data.map(el => ({
          value: el.user_id,
          label: el.name
        }))
      })
      await this.productCategory().then(response => {
        this.categories = response.data.map(el => ({
          value: el.category_id,
          label: el.category_name_en
        }))
      })
      await this.getBranch()
        .then(response => {
          this.branches = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
          // this.model.branch_id = [
          //   this.$store.state.authUser.user.default_branch.branch_id
          // ]
          this.getLocations(false)
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async getLocations($check) {
      this.loading = true
      if ($check) {
        this.model.location_id = undefined
      }
      this.locations = []
      await this.$store
        .dispatch('report/inventory/getLocation', {
          branch_id: this.model.branch_id
        })
        .then(response => {
          this.locations = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onProductSearch: _.debounce(function (value) {
      this.on_search_product = true
      this.productProfileLocation({
        filter: {
          pro_group_id: _.join(this.model.pro_group_id, ','),
          category_id: _.join(this.model.category_id, ','),
          search: value
        }
      })
        .then(response => {
          let product = response.data.map(el => ({
            value: el.product_id,
            label: el.product_name_en
          }))

          let diff = _.differenceBy(...product, this.productList, 'value')

          if (!diff.length > 0) {
            this.productList.push(...product)
          } else {
            this.productList.push(...diff)
          }

          this.productList = _.uniqBy(this.productList, 'value').splice(0, 300)
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.on_search_product = false
        })
    }, 500),
    onCategoryChange(value) {
      this.loading = true
      this.model.product_id = []
      this.model.pro_group_id = []
      this.productGroup({
        filter: {
          category_id: _.join(value, ',')
        }
      })
        .then(response => {
          this.groups = response.data.map(el => ({
            value: el.pro_group_id,
            label: el.pro_group_name_en
          }))
        })
        .finally(() => (this.loading = false))
    },
    preview() {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('report/inventory/monthlySaleItem', this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()

      this.$store
        .dispatch('report/inventory/monthlySaleItemExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    dateRangeChange(rangeDate) {
      this.$store.commit(
        'report/inventory/SET_DATE_FROM',
        rangeDate[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'report/inventory/SET_DATE_TO',
        rangeDate[1].format('DD-MM-YYYY')
      )
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit(
      'report/inventory/SET_DATE_FROM',
      moment().format('DD-MM-YYYY')
    )
    this.$store.commit(
      'report/inventory/SET_DATE_TO',
      moment().format('DD-MM-YYYY')
    )
    next()
  }
}
</script>
